import { FormEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Button } from 'shared-ui/lib/components/Button';
import { useSendPartnerServiceCallMutation } from '../../../generated/graphql';

export const combineDateAndTime = (date: string, time: string): string => {
  const combined = new Date(`${date}T${time}`);
  if (Number.isNaN(combined.getTime())) {
    throw new Error('Invalid date');
  }
  return combined.toISOString();
};

const inputClassName =
  'appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm';

const labelClassName = 'block text-sm font-medium text-gray-700 mb-1';

type VehicleFields = {
  id: string;
  name?: string | null;
  registerNumber?: string | null;
  brand?: string | null;
  model?: string | null;
};

type Props = {
  isOpen: boolean;
  vehicle: VehicleFields;
  onClose: () => void;
  onSuccess: () => void;
};

const displayValue = (value?: string | null, emptyValue = '–'): string => {
  if (!value || value.trim() === '') return emptyValue;
  return value;
};

export const ServiceCallInviteModal = ({
  isOpen,
  vehicle,
  onClose,
  onSuccess,
}: Props): JSX.Element | null => {
  const { t } = useTranslation('ServiceCallInviteModal');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [formError, setFormError] = useState('');
  const [sendPartnerServiceCall, { loading }] = useSendPartnerServiceCallMutation();

  useEffect(() => {
    if (!isOpen) return;
    setTitle('');
    setBody('');
    setDate('');
    setTime('');
    setFormError('');
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const handleClose = () => {
    if (loading) return;
    onClose();
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (loading) return;

    const trimmedTitle = title.trim();
    const trimmedBody = body.trim();
    if (!trimmedTitle || !trimmedBody || !date || !time) {
      setFormError(t('requiredError'));
      return;
    }

    let combinedDate: string;
    try {
      combinedDate = combineDateAndTime(date, time);
    } catch {
      setFormError(t('requiredError'));
      return;
    }

    const { data } = await sendPartnerServiceCall({
      variables: {
        body: {
          vehicleId: vehicle.id,
          title: trimmedTitle,
          body: trimmedBody,
          date: combinedDate,
        },
      },
    });

    if (!data?.sendPartnerServiceCall) return;

    toast.success(t('success'));
    onSuccess();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="service-call-invite-title"
        className="bg-white rounded-lg shadow-lg p-6 max-w-lg w-full mx-4"
      >
        <h2 id="service-call-invite-title" className="text-lg font-semibold text-primary">
          {t('title')}
        </h2>
        <form className="mt-4 space-y-4" onSubmit={handleSubmit}>
          <div className="grid grid-cols-2 gap-x-3 gap-y-2 text-sm">
            <p className="font-medium text-gray-700">{t('name')}</p>
            <p className="text-gray-color">{displayValue(vehicle.name, t('emptyValue'))}</p>
            <p className="font-medium text-gray-700">{t('registerNumber')}</p>
            <p className="text-gray-color">
              {displayValue(vehicle.registerNumber, t('emptyValue'))}
            </p>
            <p className="font-medium text-gray-700">{t('brand')}</p>
            <p className="text-gray-color">{displayValue(vehicle.brand, t('emptyValue'))}</p>
            <p className="font-medium text-gray-700">{t('model')}</p>
            <p className="text-gray-color">{displayValue(vehicle.model, t('emptyValue'))}</p>
          </div>
          <div>
            <label className={labelClassName} htmlFor="service-call-title">
              {t('titleLabel')}
            </label>
            <input
              id="service-call-title"
              className={inputClassName}
              type="text"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              disabled={loading}
            />
          </div>
          <div>
            <label className={labelClassName} htmlFor="service-call-body">
              {t('bodyLabel')}
            </label>
            <textarea
              id="service-call-body"
              className={inputClassName}
              rows={4}
              value={body}
              onChange={(event) => setBody(event.target.value)}
              disabled={loading}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClassName} htmlFor="service-call-date">
                {t('dateLabel')}
              </label>
              <input
                id="service-call-date"
                className={inputClassName}
                type="date"
                value={date}
                onChange={(event) => setDate(event.target.value)}
                disabled={loading}
              />
            </div>
            <div>
              <label className={labelClassName} htmlFor="service-call-time">
                {t('timeLabel')}
              </label>
              <input
                id="service-call-time"
                className={inputClassName}
                type="time"
                value={time}
                onChange={(event) => setTime(event.target.value)}
                disabled={loading}
              />
            </div>
          </div>
          {formError ? <p className="text-sm text-red-color">{formError}</p> : null}
          <div className="flex justify-end space-x-3 pt-2">
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-secondary focus:ring-offset-2 disabled:opacity-50"
            >
              {t('cancel')}
            </button>
            <Button type="submit" loading={loading} disabled={loading}>
              {t('submit')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
